import SessionDetailsItem from "@app.automotus.io/components/SessionDetailsItem";
import TextField from "@mui/material/TextField";
import React from "react";
import * as Yup from "yup";
import { Field, useField, useFormikContext } from "formik";
import { gql, useLazyQuery } from "@apollo/client";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { EnforcementIntentVehicleMakeAlias } from "common/graphql";

export const VehicleMakeSessionDetailsItem: React.FC<VehicleMakeSessionDetailsItemProps> = ({
  loading,
  vehicleMake,
  vehicleMakeAlias,
  isVehicleMakeMappingRequired,
  skeletonWidth,
  onSubmitEdit,
}) => {
  const initialValues: VehicleMakeFormValues = {
    make: vehicleMake || "",
  };

  const validationSchema = Yup.object({
    make: Yup.string().required("Make is required"),
  });

  return (
    <SessionDetailsItem
      loading={loading}
      title={"Vehicle Make"}
      content={vehicleMakeAlias?.displayName || vehicleMake}
      skeletonWidth={skeletonWidth}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmitEdit={onSubmitEdit}
    >
      {({ errors, touched }) =>
        isVehicleMakeMappingRequired ? (
          <VehicleMakeAutocomplete />
        ) : (
          <Field
            as={TextField}
            name="make"
            autoFocus
            fullWidth
            error={touched.make && Boolean(errors.make)}
            helperText={touched.make && errors.make}
          />
        )
      }
    </SessionDetailsItem>
  );
};

const GET_ENFORCEMENT_INTENT_VEHICLE_MAKE_OPTIONS = gql`
  query GetEnforcementIntentVehicleMakeOptions {
    vehicleMakeOptions: enforcement_client_workflow_vehicle_make_alias(order_by: [{ display_name: asc }]) {
      displayName: display_name
      identifiedValue: identified_value
    }
  }
`;

interface GetEnforcementIntentVehicleMakeOptionsData {
  vehicleMakeOptions: VehicleMakeOption[];
}

interface VehicleMakeOption {
  displayName: string;
  identifiedValue: string;
}

const VehicleMakeAutocomplete: React.FC = () => {
  const [loadOptions, { data: optionsData, loading: optionsLoading }] =
    useLazyQuery<GetEnforcementIntentVehicleMakeOptionsData>(GET_ENFORCEMENT_INTENT_VEHICLE_MAKE_OPTIONS);

  const { isSubmitting } = useFormikContext();
  const [field, meta, helpers] = useField<string>("make");
  const showError = meta.touched && Boolean(meta.error);

  const handleOpen = () => {
    if (!optionsData) {
      loadOptions();
    }
  };

  // TODO: we will need to test how empty values are handled
  const options = optionsData?.vehicleMakeOptions || [];

  return (
    <Autocomplete<VehicleMakeOption, false, false, false>
      fullWidth
      loading={optionsLoading}
      value={options.find((opt) => opt.identifiedValue === meta.value)}
      onOpen={handleOpen}
      onChange={(e, v) => v !== null && helpers.setValue(v.identifiedValue)}
      onBlur={field.onBlur}
      disabled={isSubmitting}
      options={options}
      isOptionEqualToValue={(option, value) => option.displayName === value.displayName}
      getOptionLabel={(opt) => opt.displayName}
      renderInput={(params) => (
        <TextField
          autoFocus
          {...params}
          name="make"
          error={showError}
          helperText={showError ? meta.error : " "}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {optionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export interface VehicleMakeFormValues {
  make: string;
}

export interface VehicleMakeSessionDetailsItemProps {
  loading?: boolean;
  vehicleMake?: string;
  vehicleMakeAlias?: EnforcementIntentVehicleMakeAlias;
  isVehicleMakeMappingRequired?: boolean;
  skeletonWidth?: number;
  onSubmitEdit?: (values: VehicleMakeFormValues) => Promise<void>;
}

export default VehicleMakeSessionDetailsItem;
